import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Button, Card, CardContainer, getSpacing, Text, TextInput, TextView } from '@rahtab/ui';
import { Screen } from '@rahtab/stack-navigator';
import useSubmitStock from 'hooks/InventoryHooks/useSubmitStock';
import { RouteProp } from '@react-navigation/core';
import { useStartStock } from 'hooks/InventoryHooks/useStartStock';
import type { StartStockResult } from 'hooks/InventoryHooks/useStartStock';
import { RootStackType } from '../../Router';
import { withNumbers, withSeparator, withToman } from '@rahtab/system/lib/tools/string';
import { useEffect } from 'react';

const NewStock: React.VFC<NewStockProps> = ({ route }) => {
  const { assignFinalCode } = route.params;
  const [stockUnitPrice, setStockUnitPrice] = useState('');
  const [stockData, setStockData] = useState<StartStockResult>();
  const [stockAmount, setStockAmount] = useState('');
  const [selected, setSelected] = useState('unitPrice');
  const { startStock, data: startStockData, loading: startStockLoading, called: startStockCalled } = useStartStock(
    assignFinalCode,
  );
  const { submitStock, loading } = useSubmitStock({
    assignFinalCode,
    amount: stockAmount,
    stockPriceUnit: stockUnitPrice,
    compileDC: stockData?.compileDC,
    stockMeasureCode: stockData?.stockMeasureCode,
    wfInstanceCode: stockData?.wfInstanceCode!,
    stateCode: stockData?.stateCode!,
  });
  const isPriceEditable = !stockData?.stockPricePerUnit;
  const calUnitPriceChange = () => {
    const s = !isNaN(parseFloat(stockUnitPrice)) ? parseFloat(stockUnitPrice) : 0;
    const p = !isNaN(parseFloat(stockData?.assignPricePerUnit || ''))
      ? parseFloat(stockData?.assignPricePerUnit || '')
      : 0;
    if (p !== 0) return Math.round(((s - p) / p) * 100);
    return 0;
  };
  const calStockPercent = () => {
    const s = !isNaN(parseFloat(stockAmount)) ? parseFloat(stockAmount) : 0;
    const p = !isNaN(parseFloat(stockData?.remainingValue || '')) ? parseFloat(stockData?.remainingValue || '') : 0;
    if (p !== 0) {
      return Math.abs(Math.round((s / p) * 100));
    }
    return 0;
  };
  useEffect(() => {
    if (!startStockCalled) startStock();
    if (!startStockLoading && !!startStockData) {
      setStockData(startStockData);
      setStockUnitPrice(startStockData.stockPricePerUnit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startStockCalled, startStockLoading]);
  return (
    <Screen
      title="ثبت عرضه جدید بار"
      loading={startStockLoading}
      transparentLoading={loading}
      renderMain={() => {
        return (
          <Card style={styles.card}>
            <CardContainer>
              <Text>اطلاعات عرضه جدید بار دریافتی را ثبت نمایید.</Text>
              <TextInput
                onChangeText={withNumbers(setStockUnitPrice)}
                value={isPriceEditable ? stockUnitPrice : withToman(stockUnitPrice)}
                editable={isPriceEditable}
                placeholder="قیمت مصرف کننده واحد کالا را وارد نمایید."
                label="قیمت مصرف کننده"
                keyboardType="numeric"
                selected={selected === 'customerPrice'}
                returnKeyType="next"
                onSubmitEditing={() => {
                  setSelected(stockUnitPrice ? 'stockAmount' : 'stockUnitPrice');
                }}
              />

              <View style={styles.rowView}>
                <TextView
                  title="قیمت واحد خرید"
                  value={withToman(stockData?.assignPricePerUnit)}
                  style={styles.biggerTextView}
                />
                <TextView
                  // title="افزایش"
                  value={`٪${calUnitPriceChange()}`}
                  valueColor={
                    parseFloat(stockUnitPrice || '0') < parseFloat(stockData?.assignPricePerUnit || '0')
                      ? 'secondary'
                      : 'primary'
                  }
                  textContainerStyle={{ justifyContent: 'center' }}
                  style={styles.seconTextView}
                />
              </View>
              <TextInput
                onChangeText={withNumbers(setStockAmount)}
                value={stockAmount}
                placeholder="مقدار عرضه بار دریافتی را مشخص نمایید."
                label="مقدار عرضه"
                keyboardType="numeric"
                selected={selected === 'stockAmount'}
                returnKeyType="done"
                onSubmitEditing={() => {
                  setSelected('');
                }}
              />
              <View style={styles.rowView}>
                <TextView
                  title="مقدار موجودی بار"
                  value={`${withSeparator(stockData?.remainingValue || '')} ${stockData?.remainingMeasureName || ''}`}
                  style={styles.biggerTextView}
                />
                <TextView
                  value={`٪${calStockPercent()}`}
                  valueColor="secondary"
                  textContainerStyle={{ justifyContent: 'center' }}
                  style={styles.seconTextView}
                />
              </View>
              <Button text="ثبت عرضه جدید بار" color="primary" onPress={submitStock} />
            </CardContainer>
          </Card>
        );
      }}
    />
  );
};

export default NewStock;

const styles = StyleSheet.create({
  card: {
    margin: getSpacing(),
  },
  rowView: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  biggerTextView: {
    flex: 3,
    marginRight: getSpacing(),
  },
  smallerTextView: {
    flex: 1,
  },
  seconTextView: {
    width: 50,
  },
});

interface NewStockProps {
  route: RouteProp<RootStackType, 'NewStock'>;
}
